import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@hooks/useStores';
import { useToastContext } from '@contexts/ToastContext';
import ActivationService from '@service/ActivationService';
import { observer } from 'mobx-react-lite';
import { MailIcon, RefreshIcon, CheckIcon } from '@components/common/ui/icons';
import * as styles from '@components/modals/ActivationBanner.module.css';

export const ActivationBanner: React.FC = observer(() => {
    const { t } = useTranslation();
    const authStore = useAuthStore();
    const toast = useToastContext();
    const [loading, setLoading] = useState(false);
    const [checking, setChecking] = useState(false);

    if (!authStore.isAuth || authStore.isOfflineMode || authStore.user?.isActivated !== false) {
        return null;
    }

    const handleResend = async () => {
        setLoading(true);
        try {
            await ActivationService.resendActivation();
            toast.success(t('activation.resendSent'));
        } catch (error: any) {
            const errorMessage =
                error?.response?.data?.message || t('activation.resendFailed');
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleCheckActivation = async () => {
        setChecking(true);
        try {
            await authStore.checkAuth();
            if (authStore.user?.isActivated) {
                toast.success(t('activation.successToast'));
            } else {
                toast.info(t('activation.notActivatedYet'));
            }
        } catch {
            toast.error(t('activation.checkStatusFailed'));
        } finally {
            setChecking(false);
        }
    };

    return (
        <div className={styles.banner}>
            <div className={styles.bannerContent}>
                <div className={styles.bannerIcon}>
                    <MailIcon className={styles.icon} />
                </div>
                <div className={styles.bannerText}>
                    <p className={styles.bannerDescription}>
                        {t('activation.bannerDescription', { email: authStore.user?.email })}
                    </p>
                </div>
                <div className={styles.bannerActions}>
                    <button onClick={handleResend} disabled={loading} className={styles.button}>
                        {loading ? (
                            <>
                                <RefreshIcon className={styles.buttonIcon} />
                                {t('activation.resending')}
                            </>
                        ) : (
                            <>
                                <RefreshIcon className={styles.buttonIcon} />
                                {t('activation.resend')}
                            </>
                        )}
                    </button>
                    <button
                        onClick={handleCheckActivation}
                        disabled={checking}
                        className={styles.button}
                    >
                        {checking ? (
                            <>
                                <CheckIcon className={styles.buttonIcon} />
                                {t('activation.checking')}
                            </>
                        ) : (
                            <>
                                <CheckIcon className={styles.buttonIcon} />
                                {t('activation.checkActivation')}
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
});
